package br.com.vvrefeicoes.portal.data.repository

import br.com.vvrefeicoes.portal.domain.model.RmpApproval
import br.com.vvrefeicoes.portal.domain.model.RmpApprovalEntry
import br.com.vvrefeicoes.portal.domain.model.RmpEvaluation
import br.com.vvrefeicoes.portal.domain.model.RmpEvaluationCriterion
import br.com.vvrefeicoes.portal.domain.model.RmpFieldCondition
import br.com.vvrefeicoes.portal.domain.model.RmpFieldKind
import br.com.vvrefeicoes.portal.domain.model.RmpFieldValue
import br.com.vvrefeicoes.portal.domain.model.RmpFormField
import br.com.vvrefeicoes.portal.domain.model.RmpFormOption
import br.com.vvrefeicoes.portal.domain.model.RmpFormSchema
import br.com.vvrefeicoes.portal.domain.model.RmpFormType
import br.com.vvrefeicoes.portal.domain.model.RmpOptionSource
import br.com.vvrefeicoes.portal.domain.model.RmpPortalOption
import br.com.vvrefeicoes.portal.domain.model.RmpRequestAttachment
import br.com.vvrefeicoes.portal.domain.model.RmpRequestDetail
import br.com.vvrefeicoes.portal.domain.model.RmpRequestSummary
import br.com.vvrefeicoes.portal.domain.model.RmpTestAttempt
import br.com.vvrefeicoes.portal.domain.model.RmpTransferChecklist
import br.com.vvrefeicoes.portal.domain.model.RmpTransferChecklistItem
import br.com.vvrefeicoes.portal.domain.model.RmpUnitRule
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import br.com.vvrefeicoes.portal.domain.repository.RmpRequestRepository
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.HttpRequestBuilder
import io.ktor.client.request.delete
import io.ktor.client.request.forms.MultiPartFormDataContent
import io.ktor.client.request.forms.formData
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.client.statement.readRawBytes
import io.ktor.http.ContentType
import io.ktor.http.Headers
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import io.ktor.http.encodeURLPathPart
import io.ktor.http.isSuccess
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.contentOrNull

class RemoteRmpRequestRepository(
    private val httpClient: HttpClient,
    private val baseUrl: String,
    private val authRepository: AuthRepository,
) : RmpRequestRepository {
    override suspend fun loadSchema(): RmpFormSchema =
        request { httpClient.get("$baseUrl/rmp/requests/schema") { authorize() } }
            .body<RmpFormSchemaDto>()
            .toDomain()

    override suspend fun loadMine(): List<RmpRequestSummary> =
        request { httpClient.get("$baseUrl/rmp/requests") { authorize() } }
            .body<List<RmpRequestSummaryDto>>()
            .map(RmpRequestSummaryDto::toDomain)

    override suspend fun load(id: String): RmpRequestDetail =
        request { httpClient.get("$baseUrl/rmp/requests/${id.encodeURLPathPart()}") { authorize() } }
            .body<RmpRequestDto>()
            .toDomain()

    override suspend fun create(
        type: String,
        status: String,
        data: JsonObject,
    ): RmpRequestDetail =
        request { httpClient.post("$baseUrl/rmp/requests") { jsonBody(RmpRequestSaveDto(type, status, data)) } }
            .body<RmpRequestDto>()
            .toDomain()

    override suspend fun update(
        id: String,
        status: String,
        data: JsonObject,
    ): RmpRequestDetail =
        request {
            httpClient.put("$baseUrl/rmp/requests/${id.encodeURLPathPart()}") {
                jsonBody(RmpRequestSaveDto(type = null, status = status, data = data))
            }
        }.body<RmpRequestDto>().toDomain()

    override suspend fun delete(id: String) {
        request { httpClient.delete("$baseUrl/rmp/requests/${id.encodeURLPathPart()}") { authorize() } }
    }

    override suspend fun uploadAttachment(
        id: String,
        field: String,
        fileName: String,
        bytes: ByteArray,
    ): RmpRequestAttachment = upload("$baseUrl/rmp/requests/${id.encodeURLPathPart()}/attachments", field, fileName, bytes)

    override suspend fun deleteAttachment(
        id: String,
        attachmentId: String,
    ) {
        request {
            httpClient.delete(
                "$baseUrl/rmp/requests/${id.encodeURLPathPart()}/attachments/${attachmentId.encodeURLPathPart()}",
            ) { authorize() }
        }
    }

    override suspend fun downloadAttachment(
        id: String,
        attachmentId: String,
    ): ByteArray =
        request {
            httpClient.get(
                "$baseUrl/rmp/requests/${id.encodeURLPathPart()}/attachments/${attachmentId.encodeURLPathPart()}",
            ) { authorize() }
        }.readRawBytes()

    override suspend fun loadApproval(id: String): RmpApproval =
        request { httpClient.get("$baseUrl/rmp/approvals/${id.encodeURLPathPart()}") { authorize() } }
            .body<RmpApprovalDto>()
            .toDomain()

    override suspend fun approve(
        id: String,
        note: String?,
    ): RmpApproval = decide(id, "approve", note)

    override suspend fun reject(
        id: String,
        note: String?,
    ): RmpApproval = decide(id, "reject", note)

    override suspend fun downloadApprovalAttachment(
        id: String,
        attachmentId: String,
    ): ByteArray =
        request {
            httpClient.get(
                "$baseUrl/rmp/approvals/${id.encodeURLPathPart()}/attachments/${attachmentId.encodeURLPathPart()}",
            ) { authorize() }
        }.readRawBytes()

    override suspend fun loadEditSchema(id: String): RmpFormSchema =
        request { httpClient.get("$baseUrl/rmp/approvals/${id.encodeURLPathPart()}/form") { authorize() } }
            .body<RmpFormSchemaDto>()
            .toDomain()

    override suspend fun updateSubmitted(
        id: String,
        data: JsonObject,
    ): RmpApproval =
        request {
            httpClient.put("$baseUrl/rmp/approvals/${id.encodeURLPathPart()}") {
                jsonBody(RmpRequestSaveDto(type = null, status = null, data = data))
            }
        }.body<RmpApprovalDto>().toDomain()

    override suspend fun deleteSubmitted(id: String) {
        request { httpClient.delete("$baseUrl/rmp/approvals/${id.encodeURLPathPart()}") { authorize() } }
    }

    override suspend fun uploadSubmittedAttachment(
        id: String,
        field: String,
        fileName: String,
        bytes: ByteArray,
    ): RmpRequestAttachment = upload("$baseUrl/rmp/approvals/${id.encodeURLPathPart()}/attachments", field, fileName, bytes)

    override suspend fun deleteSubmittedAttachment(
        id: String,
        attachmentId: String,
    ) {
        request {
            httpClient.delete(
                "$baseUrl/rmp/approvals/${id.encodeURLPathPart()}/attachments/${attachmentId.encodeURLPathPart()}",
            ) { authorize() }
        }
    }

    override suspend fun saveTransferChecklist(
        id: String,
        items: Map<String, String>,
        observation: String?,
    ): RmpApproval =
        request {
            httpClient.put("$baseUrl/rmp/approvals/${id.encodeURLPathPart()}/transfer-checklist") {
                jsonBody(RmpTransferChecklistSaveDto(items, observation?.trim()?.takeIf { it.isNotEmpty() }))
            }
        }.body<RmpApprovalDto>().toDomain()

    override suspend fun saveEvaluation(
        id: String,
        data: JsonObject,
    ): RmpApproval =
        request {
            httpClient.put("$baseUrl/rmp/approvals/${id.encodeURLPathPart()}/evaluation") {
                jsonBody(RmpEvaluationSaveDto(data))
            }
        }.body<RmpApprovalDto>().toDomain()

    override suspend fun convokeTestCandidate(
        id: String,
        data: JsonObject,
    ): RmpApproval =
        request {
            httpClient.post("$baseUrl/rmp/approvals/${id.encodeURLPathPart()}/test-attempts") {
                jsonBody(RmpTestAttemptSaveDto(data))
            }
        }.body<RmpApprovalDto>().toDomain()

    override suspend fun recordTestResult(
        id: String,
        attemptId: String,
        result: String,
    ): RmpApproval =
        request {
            httpClient.post(
                "$baseUrl/rmp/approvals/${id.encodeURLPathPart()}/test-attempts/${attemptId.encodeURLPathPart()}/result",
            ) { jsonBody(RmpTestResultDto(result)) }
        }.body<RmpApprovalDto>().toDomain()

    override suspend fun downloadConvocation(
        id: String,
        attemptId: String,
    ): ByteArray =
        request {
            httpClient.get(
                "$baseUrl/rmp/approvals/${id.encodeURLPathPart()}/test-attempts/${attemptId.encodeURLPathPart()}/convocation",
            ) { authorize() }
        }.readRawBytes()

    override suspend fun reopen(
        id: String,
        decisionIds: List<String>,
    ): RmpApproval =
        request {
            httpClient.post("$baseUrl/rmp/approvals/${id.encodeURLPathPart()}/reopen") { jsonBody(RmpReopenDto(decisionIds)) }
        }.body<RmpApprovalDto>().toDomain()

    override suspend fun replaceCandidate(
        id: String,
        fields: Map<String, String>,
        reason: String,
    ): RmpApproval =
        request {
            httpClient.post("$baseUrl/rmp/approvals/${id.encodeURLPathPart()}/replace-candidate") {
                jsonBody(
                    RmpCandidateReplacementDto(
                        nome = fields["nome"].orEmpty(),
                        cargo = fields["cargo"]?.takeIf { it.isNotBlank() },
                        turno = fields["turno"]?.takeIf { it.isNotBlank() },
                        salario = fields["salario"]?.takeIf { it.isNotBlank() },
                        gratificacao = fields["gratificacao"]?.takeIf { it.isNotBlank() },
                        motivo = reason.trim().takeIf { it.isNotEmpty() },
                    ),
                )
            }
        }.body<RmpApprovalDto>().toDomain()

    override suspend fun markApt(id: String): RmpApproval =
        request { httpClient.post("$baseUrl/rmp/approvals/${id.encodeURLPathPart()}/apt") { authorize() } }
            .body<RmpApprovalDto>()
            .toDomain()

    override suspend fun downloadDocument(id: String): ByteArray =
        request { httpClient.get("$baseUrl/rmp/approvals/${id.encodeURLPathPart()}/document") { authorize() } }
            .readRawBytes()

    override suspend fun loadUnitRules(): List<RmpUnitRule> =
        request { httpClient.get("$baseUrl/rmp/unit-rules") { authorize() } }
            .body<List<RmpUnitRuleDto>>()
            .map(RmpUnitRuleDto::toDomain)

    override suspend fun updateUnitRule(
        companyId: String,
        admissionWaivesCeo: Boolean,
    ): RmpUnitRule =
        request {
            httpClient.put("$baseUrl/rmp/unit-rules/${companyId.encodeURLPathPart()}") {
                jsonBody(RmpUnitRuleSaveDto(admissionWaivesCeo))
            }
        }.body<RmpUnitRuleDto>().toDomain()

    private suspend fun upload(
        url: String,
        field: String,
        fileName: String,
        bytes: ByteArray,
    ): RmpRequestAttachment =
        request {
            httpClient.post(url) {
                authorize()
                setBody(
                    MultiPartFormDataContent(
                        formData {
                            append("field", field)
                            append(
                                "file",
                                bytes,
                                Headers.build {
                                    append(HttpHeaders.ContentType, "application/pdf")
                                    append(HttpHeaders.ContentDisposition, "filename=\"${fileName.replace("\"", "")}\"")
                                },
                            )
                        },
                    ),
                )
            }
        }.body<RmpRequestAttachmentDto>().toDomain()

    private suspend fun decide(
        id: String,
        action: String,
        note: String?,
    ): RmpApproval =
        request {
            httpClient.post("$baseUrl/rmp/approvals/${id.encodeURLPathPart()}/$action") {
                jsonBody(RmpApprovalDecisionDto(note?.trim()?.takeIf { it.isNotEmpty() }))
            }
        }.body<RmpApprovalDto>().toDomain()

    private suspend inline fun request(crossinline call: suspend () -> HttpResponse): HttpResponse {
        val response = call()
        if (!response.status.isSuccess()) {
            val message =
                runCatching {
                    Json { ignoreUnknownKeys = true }
                        .decodeFromString<RmpRequestErrorDto>(response.bodyAsText())
                        .message
                }.getOrNull()
            error(message ?: "HTTP ${response.status.value}")
        }
        return response
    }

    private fun HttpRequestBuilder.authorize() {
        authRepository.session.value?.token?.let { token ->
            header(HttpHeaders.Authorization, "Bearer $token")
        }
    }

    private fun HttpRequestBuilder.jsonBody(body: Any) {
        authorize()
        contentType(ContentType.Application.Json)
        setBody(body)
    }
}

@Serializable
private data class RmpRequestSaveDto(
    val type: String?,
    val status: String?,
    val data: JsonObject,
)

@Serializable
private data class RmpFormSchemaDto(
    val types: List<RmpFormTypeDto> = emptyList(),
    val linkedUnits: List<RmpUnitOptionDto> = emptyList(),
    val allUnits: List<RmpUnitOptionDto> = emptyList(),
) {
    fun toDomain(): RmpFormSchema =
        RmpFormSchema(
            types = types.map(RmpFormTypeDto::toDomain),
            linkedUnits = linkedUnits.map(RmpUnitOptionDto::toDomain),
            allUnits = allUnits.map(RmpUnitOptionDto::toDomain),
        )
}

@Serializable
private data class RmpFormTypeDto(
    val value: String,
    val label: String,
    val fields: List<RmpFormFieldDto> = emptyList(),
) {
    fun toDomain(): RmpFormType = RmpFormType(value, label, fields.map(RmpFormFieldDto::toDomain))
}

@Serializable
private data class RmpFormFieldDto(
    val name: String? = null,
    val label: String = "",
    val kind: String = "",
    val required: Boolean = false,
    val options: List<RmpFormOptionDto> = emptyList(),
    val source: String? = null,
    val showWhen: RmpFieldConditionDto? = null,
    val requiredWhen: RmpFieldConditionDto? = null,
    val placeholder: String? = null,
    val note: String? = null,
    val maxLength: Int? = null,
    val allowZero: Boolean = false,
    val readOnly: Boolean = false,
    val defaultValue: String? = null,
) {
    fun toDomain(): RmpFormField =
        RmpFormField(
            name = name,
            label = label,
            kind = RmpFieldKind.entries.firstOrNull { it.name == kind } ?: RmpFieldKind.UNKNOWN,
            required = required,
            options = options.map { RmpFormOption(it.value, it.label) },
            source = RmpOptionSource.entries.firstOrNull { it.name == source },
            showWhen = showWhen?.let { RmpFieldCondition(it.field, it.value) },
            requiredWhen = requiredWhen?.let { RmpFieldCondition(it.field, it.value) },
            placeholder = placeholder,
            note = note,
            maxLength = maxLength,
            allowZero = allowZero,
            readOnly = readOnly,
            defaultValue = defaultValue,
        )
}

@Serializable
private data class RmpFormOptionDto(
    val value: String,
    val label: String,
)

@Serializable
private data class RmpFieldConditionDto(
    val field: String,
    val value: String,
)

@Serializable
private data class RmpUnitOptionDto(
    val id: String,
    val name: String? = null,
) {
    fun toDomain(): RmpPortalOption = RmpPortalOption(id = id, name = name.orEmpty())
}

@Serializable
private data class RmpRequestAttachmentDto(
    val id: String,
    val field: String,
    val fileName: String = "",
    val sizeBytes: Long = 0,
) {
    fun toDomain(): RmpRequestAttachment = RmpRequestAttachment(id, field, fileName, sizeBytes)
}

@Serializable
private data class RmpRequestDto(
    val id: String,
    val number: Long? = null,
    val type: String,
    val typeLabel: String? = null,
    val status: String,
    val companyName: String? = null,
    val data: JsonObject = JsonObject(emptyMap()),
    val attachments: List<RmpRequestAttachmentDto> = emptyList(),
) {
    fun toDomain(): RmpRequestDetail =
        RmpRequestDetail(
            id = id,
            number = number,
            type = type,
            typeLabel = typeLabel,
            status = status,
            companyName = companyName,
            data = data,
            attachments = attachments.map(RmpRequestAttachmentDto::toDomain),
        )
}

@Serializable
private data class RmpRequestSummaryDto(
    val id: String,
    val number: Long? = null,
    val type: String,
    val typeLabel: String? = null,
    val status: String,
    val companyName: String? = null,
    val employeeName: String? = null,
    val createdAt: String? = null,
    val submittedAt: String? = null,
) {
    fun toDomain(): RmpRequestSummary = RmpRequestSummary(id, number, type, typeLabel, status, companyName, employeeName, createdAt, submittedAt)
}

@Serializable
private data class RmpApprovalDecisionDto(
    val note: String?,
)

@Serializable
private data class RmpFieldValueDto(
    val label: String = "",
    val value: String? = null,
    val section: Boolean = false,
)

@Serializable
private data class RmpApprovalEntryDto(
    val id: String? = null,
    val stageLabel: String? = null,
    val decision: String = "",
    val note: String? = null,
    val decidedByName: String? = null,
    val decidedAt: String? = null,
)

@Serializable
private data class RmpApprovalDto(
    val request: RmpRequestDto,
    val fields: List<RmpFieldValueDto> = emptyList(),
    val requesterName: String? = null,
    val waitingStageLabel: String? = null,
    val waitingName: String? = null,
    val canDecide: Boolean = false,
    val canEdit: Boolean = false,
    val canDelete: Boolean = false,
    val missingFields: List<String> = emptyList(),
    val transferChecklist: RmpTransferChecklistDto? = null,
    val evaluation: RmpEvaluationDto? = null,
    val canEvaluate: Boolean = false,
    val testAttempts: List<RmpTestAttemptDto>? = null,
    val canConvoke: Boolean = false,
    val canRecordTestResult: Boolean = false,
    val hrStatus: String? = null,
    val canReopen: Boolean = false,
    val canReplaceCandidate: Boolean = false,
    val canMarkApt: Boolean = false,
    val approvals: List<RmpApprovalEntryDto> = emptyList(),
) {
    fun toDomain(): RmpApproval =
        RmpApproval(
            request = request.toDomain(),
            fields = fields.map { RmpFieldValue(it.label, it.value, it.section) },
            requesterName = requesterName,
            waitingStageLabel = waitingStageLabel,
            waitingName = waitingName,
            canDecide = canDecide,
            approvals = approvals.map { RmpApprovalEntry(it.stageLabel, it.decision, it.note, it.decidedByName, it.decidedAt, it.id) },
            canEdit = canEdit,
            canDelete = canDelete,
            missingFields = missingFields,
            transferChecklist = transferChecklist?.toDomain(),
            evaluation = evaluation?.toDomain(),
            canEvaluate = canEvaluate,
            testAttempts = testAttempts?.map(RmpTestAttemptDto::toDomain),
            canConvoke = canConvoke,
            canRecordTestResult = canRecordTestResult,
            hrStatus = hrStatus,
            canReopen = canReopen,
            canReplaceCandidate = canReplaceCandidate,
            canMarkApt = canMarkApt,
        )
}

@Serializable
private data class RmpReopenDto(
    val decisionIds: List<String>,
)

@Serializable
private data class RmpCandidateReplacementDto(
    val nome: String,
    val cargo: String?,
    val turno: String?,
    val salario: String?,
    val gratificacao: String?,
    val motivo: String?,
)

@Serializable
private data class RmpTestAttemptDto(
    val id: String,
    val data: JsonObject = JsonObject(emptyMap()),
    val result: String = "",
    val createdByName: String? = null,
    val createdAt: String? = null,
) {
    fun toDomain(): RmpTestAttempt =
        RmpTestAttempt(
            id = id,
            data = data.mapValues { (_, value) -> (value as? JsonPrimitive)?.contentOrNull.orEmpty() },
            result = result,
            createdByName = createdByName,
            createdAt = createdAt,
        )
}

@Serializable
private data class RmpTestAttemptSaveDto(
    val data: JsonObject,
)

@Serializable
private data class RmpTestResultDto(
    val result: String,
)

@Serializable
private data class RmpEvaluationCriterionDto(
    val key: String,
    val group: String = "",
    val label: String = "",
    val score: Int? = null,
)

@Serializable
private data class RmpEvaluationDto(
    val done: Boolean = false,
    val data: JsonObject = JsonObject(emptyMap()),
    val criteria: List<RmpEvaluationCriterionDto> = emptyList(),
    val average: Double? = null,
    val evaluatorName: String? = null,
    val updatedAt: String? = null,
) {
    fun toDomain(): RmpEvaluation =
        RmpEvaluation(
            done = done,
            data = data.mapValues { (_, value) -> (value as? JsonPrimitive)?.contentOrNull.orEmpty() },
            criteria = criteria.map { RmpEvaluationCriterion(it.key, it.group, it.label, it.score) },
            average = average,
            evaluatorName = evaluatorName,
            updatedAt = updatedAt,
        )
}

@Serializable
private data class RmpEvaluationSaveDto(
    val data: JsonObject,
)

@Serializable
private data class RmpTransferChecklistItemDto(
    val key: String,
    val label: String = "",
    val value: String? = null,
)

@Serializable
private data class RmpTransferChecklistDto(
    val items: List<RmpTransferChecklistItemDto> = emptyList(),
    val observation: String? = null,
    val filledByName: String? = null,
    val filledAt: String? = null,
    val complete: Boolean = false,
) {
    fun toDomain(): RmpTransferChecklist =
        RmpTransferChecklist(
            items = items.map { RmpTransferChecklistItem(it.key, it.label, it.value) },
            observation = observation,
            filledByName = filledByName,
            filledAt = filledAt,
            complete = complete,
        )
}

@Serializable
private data class RmpTransferChecklistSaveDto(
    val items: Map<String, String>,
    val observation: String?,
)

@Serializable
private data class RmpUnitRuleDto(
    val companyId: String,
    val companyName: String? = null,
    val admissionWaivesCeo: Boolean = false,
) {
    fun toDomain(): RmpUnitRule = RmpUnitRule(companyId, companyName.orEmpty(), admissionWaivesCeo)
}

@Serializable
private data class RmpUnitRuleSaveDto(
    val admissionWaivesCeo: Boolean,
)

@Serializable
private data class RmpRequestErrorDto(
    val message: String? = null,
)
